import React, { useMemo, useState } from 'react';
import {
  Card,
  Descriptions,
  Button,
  Tag,
  Alert,
  Space,
  Row,
  Col,
  Select,
  Typography,
  message,
  Radio,
  Input,
  Steps,
  Collapse,
  Divider,
} from 'antd';
import { CreditCardOutlined, RocketOutlined, SwapOutlined, BankOutlined, FileTextOutlined, MailOutlined, CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { z } from 'zod';
import { trpc } from '../../lib/trpc';
import { featureFlags } from '../../utils/featureFlags';
import type { Company } from '../../types/company';
import { useNavigate } from 'react-router-dom';
import { BANK_TRANSFER_STORAGE_KEY } from './bankTransferConstants';
import { logger } from '../../utils/logger';
import {
  calculateDisplayState,
  getBankTransferStatusUi,
  getSubscriptionStatusColor,
  getSubscriptionStatusLabel,
} from './subscriptionStateHelpers';
import type { ManagedPlanCode } from '@shared/config/subscription-plans';
import {
  ENTERPRISE_PREMIUM_ENABLED,
  MANAGED_PLAN_ORDER,
  DEFAULT_TAX_RATE,
  SUBSCRIPTION_PLAN_CONFIGS,
} from '@shared/config/subscription-plans';
import { SubscriptionCardPaymentModal } from '../subscription/SubscriptionCardPaymentModal';
import PlanChangeConfirmModal from './PlanChangeConfirmModal';
import {
  getSubscriptionProcessing,
  setSubscriptionProcessing,
  clearSubscriptionProcessing,
  type SubscriptionProcessingState,
} from '../../utils/subscriptionProcessingLock';
import { useLatestCancellationRequest } from '../../hooks/useCompanyState';

const { Paragraph, Text } = Typography;

/**
 * 日割り額を計算（Stripeの計算ロジックに準拠）
 *
 * Stripeはタイムスタンプ（秒単位）で日割り計算を行う：
 * - 分子: 現在時刻から月末23:59:59までの秒数
 * - 分母: 次の請求サイクル（翌月）の総秒数
 *
 * @param monthlyAmount - 月額料金（税抜）
 * @returns 日割り額（税抜）
 */
function calculateProrationAmount(monthlyAmount: number): number {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed (0=1月, 11=12月)

  // 月末の23:59:59（billing_cycle_anchor）
  const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59, 999);

  // 現在時刻から月末までの残り秒数
  const remainingMs = endOfMonth.getTime() - now.getTime();
  const remainingSeconds = Math.max(0, remainingMs / 1000);

  // 翌月の日数を計算（年またぎ対応）
  const nextMonth = month + 1;
  const nextYear = nextMonth > 11 ? year + 1 : year;
  const normalizedNextMonth = nextMonth > 11 ? 0 : nextMonth;
  const daysInNextBillingCycle = new Date(nextYear, normalizedNextMonth + 1, 0).getDate();

  // 翌月の総秒数
  const totalSecondsInNextCycle = daysInNextBillingCycle * 24 * 60 * 60;

  // 日割り計算: Stripeと同じタイムスタンプベースの計算
  const prorationAmount = Math.round((remainingSeconds / totalSecondsInNextCycle) * monthlyAmount);

  return prorationAmount;
}

interface BaseContractPanelProps {
  company?: Company;
  isManager: boolean;
  currentUser?: {
    id: string;
    email?: string | null;
    name?: string | null;
    role?: string | null;
  };
  hasStoredBankTransferResult: boolean;
  bankTransferLastSavedAt: string | null;
  onSwitchToTab?: (tabKey: string) => void; // [v2.1] 追加
}

interface PlanDefinition {
  name: string;
  price: number | null;
  color: string;
  features: string[];
  description?: string;
}

const resolvePlanConfig = (code: ManagedPlanCode) => SUBSCRIPTION_PLAN_CONFIGS[code];

const PLAN_DEFINITIONS: Record<string, PlanDefinition> = {
  free: {
    name: 'フリー',
    price: 0,
    color: 'default',
    features: ['基本ダッシュボード', '設備登録 10台まで'],
    description: '評価やPoC用途向けの無料プラン',
  },
  starter: {
    name: resolvePlanConfig('starter').label,
    price: resolvePlanConfig('starter').monthlyPrice,
    color: 'blue',
    features: resolvePlanConfig('starter').featureHighlights,
    description: resolvePlanConfig('starter').shortDescription,
  },
  standard: {
    name: resolvePlanConfig('standard').label,
    price: resolvePlanConfig('standard').monthlyPrice,
    color: 'cyan',
    features: resolvePlanConfig('standard').featureHighlights,
    description: resolvePlanConfig('standard').shortDescription,
  },
  professional: {
    name: resolvePlanConfig('professional').label,
    price: resolvePlanConfig('professional').monthlyPrice,
    color: 'geekblue',
    features: resolvePlanConfig('professional').featureHighlights,
    description: resolvePlanConfig('professional').shortDescription,
  },
  premium: {
    name: 'プレミアム',
    price: 98_000,
    color: 'gold',
    features: ['設備登録 500台まで', '24時間サポート'],
    description: '旧プレミアムプラン (段階的に廃止予定)',
  },
  enterprise: {
    name: resolvePlanConfig('enterprise').label,
    price: resolvePlanConfig('enterprise').monthlyPrice,
    color: 'gold',
    features: resolvePlanConfig('enterprise').featureHighlights,
    description: resolvePlanConfig('enterprise').shortDescription,
  },
  enterprise_premium: {
    name: resolvePlanConfig('enterprise_premium').label,
    price: resolvePlanConfig('enterprise_premium').monthlyPrice,
    color: 'magenta',
    features: resolvePlanConfig('enterprise_premium').featureHighlights,
    description: resolvePlanConfig('enterprise_premium').shortDescription,
  },
};

const MANAGEABLE_PLAN_CODES = MANAGED_PLAN_ORDER;
const DEFAULT_PLAN_KEY: ManagedPlanCode = 'starter';
const MANAGEABLE_PLAN_CODE_SET = new Set<ManagedPlanCode>(MANAGEABLE_PLAN_CODES);
const DISPLAY_PLAN_CODE_SET = new Set<string>(Object.keys(PLAN_DEFINITIONS));

const resolveManagedPlanCode = (value?: string | null): ManagedPlanCode =>
  MANAGEABLE_PLAN_CODE_SET.has(value as ManagedPlanCode)
    ? (value as ManagedPlanCode)
    : DEFAULT_PLAN_KEY;

const resolveDisplayPlanCode = (value?: string | null): string => {
  const normalized = value?.trim();
  return normalized && DISPLAY_PLAN_CODE_SET.has(normalized) ? normalized : DEFAULT_PLAN_KEY;
};

const MESSAGES = {
  planUpdateSuccess: '契約プランを変更しました',
  planUpdateFailure: '契約プランの変更に失敗しました。時間をおいて再度お試しください。',
  checkoutStartFailure: 'Stripeチェックアウトの開始に失敗しました。時間をおいて再度お試しください。',
  bankTransferSuccess: '銀行振込での申し込みを受け付けました。振込先情報をご確認ください。',
  cancellationPending: '解約申請中のため、プラン変更や追加オプションの追加はできません',  // [v2.1] 追加
  bankTransferFailure: '銀行振込のお申し込みに失敗しました。時間をおいて再度お試しください。',
  stripeNotLinked: 'Stripe契約が未連携です。先に「Stripeでサブスク契約を開始」を実行してください。',
  noPlanChange: '契約プランに変更はありません。',
  invalidPlanSelection: '申し込み可能なプランを選択してください。',
  companyNotLoaded: '会社情報が取得できませんでした。ページを再読み込みしてください。',
  missingBillingEmail: '請求先メールアドレスが未設定です。請求先メールアドレスを登録してください。',
  missingTaxNumber: '法人番号が未設定です。法人番号を登録してください。',
  billingEmailNotVerified: '請求先メールアドレスの確認が完了していません。確認メールを送信して確認を完了してください。',
  blockedPlanChange: '現在の契約状態ではプランを変更できません。支払い状況をご確認ください。',
  bankTransferPending: '銀行振込のお手続き中です。入金が完了するまでお待ちください。',
  billingEmailUpdateSuccess: '請求先メールアドレスを更新しました',
  billingEmailUpdateFailure: '請求先メールアドレスの更新に失敗しました',
  taxNumberUpdateSuccess: '法人番号を更新しました',
  taxNumberUpdateFailure: '法人番号の更新に失敗しました',
  invalidBillingEmail: 'メールアドレスは「example@company.co.jp」のような形式で入力してください',
  invalidTaxNumber: '法人番号は13桁の数字で入力してください（例: 1234567890123）',
  verificationEmailSent: '確認メールを送信しました。受信トレイをご確認ください。',
  verificationEmailFailure: '確認メールの送信に失敗しました',
  verificationSuccess: 'メールアドレスを確認しました！',
  verificationCodeInvalid: '確認コードが正しくありません',
  verificationLinkInvalid: '確認リンクが無効または期限切れです',
};

type PaymentMethodOption = 'credit_card' | 'bank_transfer';

const bankTransferRequestSchema = z.object({
  planCode: z
    .string()
    .refine((value) => MANAGEABLE_PLAN_CODE_SET.has(value as ManagedPlanCode), {
      message: '銀行振込に対応していないプランが選択されています。',
    }),
  customerEmail: z.string().email('請求先メールアドレスを入力してください。'),
  customerName: z.string().min(1, '請求先の宛名を入力してください。'),
  companyId: z.string().uuid(),
  predictionAddonQuantity: z.number().int().min(0).max(1000),
  userAddonQuantity: z.number().int().min(0).max(1000),
  daysUntilDue: z.number().int().min(1).max(90),
});

function getPlanDetails(planCode: string): PlanDefinition {
  return PLAN_DEFINITIONS[planCode] ?? PLAN_DEFINITIONS[DEFAULT_PLAN_KEY];
}

const monthlyPriceCurrencyFormatter = new Intl.NumberFormat('ja-JP', {
  style: 'currency',
  currency: 'JPY',
  maximumFractionDigits: 0,
});

const monthlyPriceNumberFormatter = new Intl.NumberFormat('ja-JP', {
  maximumFractionDigits: 0,
});

type PriceParts = {
  base: string;
  tax?: string;
};

function getMonthlyPriceParts(
  definition: PlanDefinition,
  options: { includeSuffix?: boolean } = {},
): PriceParts {
  const includeSuffix = options.includeSuffix !== false;
  const suffix = includeSuffix ? '/月' : '';

  if (definition.price === null) {
    return { base: 'お見積り' };
  }

  const base = `${monthlyPriceCurrencyFormatter.format(definition.price)}${suffix}`;
  const taxIncluded = Math.round(definition.price * (1 + DEFAULT_TAX_RATE));
  const tax = `税込${monthlyPriceNumberFormatter.format(taxIncluded)}円${suffix}`;

  return { base, tax };
}

function formatMonthlyPrice(
  definition: PlanDefinition,
  options: { includeSuffix?: boolean } = {},
): string {
  const parts = getMonthlyPriceParts(definition, options);
  return parts.tax ? `${parts.base} (${parts.tax})` : parts.base;
}

function formatDateTimeForDisplay(value?: string | null) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// メールバリデーションスキーマ
const emailSchema = z.string().email('有効なメールアドレスを入力してください');

const isValidEmail = (email: string): boolean => {
  if (!email) return false;
  try {
    emailSchema.parse(email.trim());
    return true;
  } catch {
    return false;
  }
};

// 法人番号バリデーションスキーマ（日本の法人番号は13桁）
const taxNumberSchema = z.string().regex(/^\d{13}$/, '法人番号は13桁の数字です');

const isValidTaxNumber = (taxNumber: string): boolean => {
  if (!taxNumber) return false;
  try {
    taxNumberSchema.parse(taxNumber.trim());
    return true;
  } catch {
    return false;
  }
};

export const BaseContractPanel: React.FC<BaseContractPanelProps> = ({
  company,
  isManager,
  currentUser,
  hasStoredBankTransferResult,
  bankTransferLastSavedAt,
  onSwitchToTab, // [v2.1] 追加
}) => {
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<ManagedPlanCode>(() =>
    resolveManagedPlanCode(company?.subscriptionPlan),
  );
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodOption>('credit_card');
  const [cardPaymentModalOpen, setCardPaymentModalOpen] = useState(false);
  const [messageApi, messageContextHolder] = message.useMessage();

  // 🆕 契約処理中フラグの状態
  const [isSubscriptionProcessing, setIsSubscriptionProcessing] = useState<boolean>(false);
  const [processingState, setProcessingState] = useState<SubscriptionProcessingState | null>(null);

  // 請求先メール編集用の状態
  const [editingBillingEmail, setEditingBillingEmail] = useState<string>('');
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  // 法人番号編集用の状態
  const [editingTaxNumber, setEditingTaxNumber] = useState<string>('');

  // ご紹介コード関連の状態
  const [promotionCode, setPromotionCode] = useState<string>('');
  const [promotionCodeValidated, setPromotionCodeValidated] = useState<boolean>(false);
  const [promotionCodeValid, setPromotionCodeValid] = useState<boolean | null>(null);
  const [promotionCodeMessage, setPromotionCodeMessage] = useState<string>('');
  const [promotionCodeDiscount, setPromotionCodeDiscount] = useState<{
    percentOff: number | null;
    amountOff: number | null;
    currency: string | null;
    duration: string;
    durationInMonths: number | null;
  } | null>(null);

  // プラン変更確認モーダル
  const [planChangeConfirmOpen, setPlanChangeConfirmOpen] = useState(false);

  // 解約申請状態を取得
  const { latestRequest } = useLatestCancellationRequest(company?.id);
  // 解約申請中かどうか（subscriptionStatus または latestRequest で判定）
  const isCancellationPending = company?.subscriptionStatus === 'canceling' || latestRequest?.status === 'pending';

  // Collapseの開閉状態管理
  const [activeCollapseKeys, setActiveCollapseKeys] = useState<string[]>(() => {
    // 初期値: Step1が未完了なら開く
    const step1Completed = company?.billingEmailVerified && company?.taxNumber;
    if (!step1Completed) {
      return ['billing-info'];
    }
    // Step1完了 && 未契約ならStep2を開く
    const hasStripeSubscription = !!company?.stripeSubscriptionId;
    if (step1Completed && !hasStripeSubscription) {
      return ['plan-selection'];
    }
    return [];
  });

  const displayState = useMemo(
    () =>
      company
        ? calculateDisplayState(company, { hasStoredBankTransferResult })
        : null,
    [company, hasStoredBankTransferResult],
  );

  const currentPlanKey = useMemo(
    () => resolveManagedPlanCode(company?.subscriptionPlan),
    [company?.subscriptionPlan],
  );

  const currentDisplayPlanKey = useMemo(
    () => resolveDisplayPlanCode(company?.subscriptionPlan),
    [company?.subscriptionPlan],
  );

  const currentPlan = useMemo(() => getPlanDetails(currentDisplayPlanKey), [currentDisplayPlanKey]);
  const selectedPlanDetails = useMemo(() => getPlanDetails(selectedPlan), [selectedPlan]);
  const currentPlanPriceParts = useMemo(
    () => getMonthlyPriceParts(currentPlan),
    [currentPlan],
  );
  const selectedPlanPriceParts = useMemo(
    () => getMonthlyPriceParts(selectedPlanDetails),
    [selectedPlanDetails],
  );
  const planOptions = useMemo(
    () =>
      MANAGEABLE_PLAN_CODES.map((code) => {
        const definition = getPlanDetails(code);
        const priceLabel = formatMonthlyPrice(definition);
        return { value: code, label: `${definition.name} ${priceLabel}` };
      }),
    [],
  );

  const bankTransferStatusValue = company?.bankTransferStatus ?? null;
  const bankTransferStatusUi = useMemo(
    () => getBankTransferStatusUi(bankTransferStatusValue, { hasStoredBankTransferResult }),
    [bankTransferStatusValue, hasStoredBankTransferResult],
  );

  const bankTransferStatusUpdatedAt = useMemo(() => {
    if (!displayState) {
      return null;
    }
    if (displayState.bankTransferStatus === 'confirmed' && displayState.bankTransferConfirmedAt) {
      return displayState.bankTransferConfirmedAt;
    }
    if (hasStoredBankTransferResult && bankTransferLastSavedAt) {
      return bankTransferLastSavedAt;
    }
    return null;
  }, [displayState, hasStoredBankTransferResult, bankTransferLastSavedAt]);

  const showBankTransferStatusCard = Boolean(
    bankTransferStatusUi ||
    company?.bankTransferMemo ||
    displayState?.showBankTransferInstructions ||
    hasStoredBankTransferResult,
  );
  const bankTransferStatusUpdatedLabel = formatDateTimeForDisplay(bankTransferStatusUpdatedAt);
  const bankTransferSavedLabel = formatDateTimeForDisplay(bankTransferLastSavedAt);

  const planChanged = selectedPlan !== currentPlanKey;
  const hasStripeSubscription = displayState?.hasStripeSubscription ?? false;
  const hasPendingBankTransfer = displayState?.hasPendingBankTransfer ?? false;
  const canSelectPaymentMethod = displayState?.canSelectPaymentMethod ?? false;
  const canChangePlan = displayState?.canChangePlan ?? false;
  const planSelectionValid = MANAGEABLE_PLAN_CODES.includes(selectedPlan);

  const preferredCustomerEmail = useMemo(() => {
    const candidates = [
      company?.billingContactEmail,
      company?.billingEmail,
      company?.email,
      currentUser?.email ?? undefined,
    ];
    const email = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
    return email?.trim() ?? '';
  }, [company?.billingContactEmail, company?.billingEmail, company?.email, currentUser?.email]);

  const preferredCustomerName = useMemo(() => {
    const candidates = [
      company?.billingContactName,
      company?.name,
      currentUser?.name ?? undefined,
    ];
    const name = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
    return name?.trim() ?? '';
  }, [company?.billingContactName, company?.name, currentUser?.name]);

  React.useEffect(() => {
    if (!company) {
      return;
    }
    const planKey = resolveManagedPlanCode(company.subscriptionPlan);
    setSelectedPlan(planKey);
  }, [company]);

  React.useEffect(() => {
    if (!displayState) {
      return;
    }
    if (!displayState.canSelectPaymentMethod) {
      setPaymentMethod(displayState.paymentMethod === 'bank_transfer' ? 'bank_transfer' : 'credit_card');
    }
  }, [displayState]);

  // 請求先メールの初期化
  React.useEffect(() => {
    if (company?.billingEmail) {
      setEditingBillingEmail(company.billingEmail);
    } else if (company?.billingContactEmail) {
      setEditingBillingEmail(company.billingContactEmail);
    }
  }, [company?.billingEmail, company?.billingContactEmail]);

  // 法人番号の初期化
  React.useEffect(() => {
    if (company?.taxNumber) {
      setEditingTaxNumber(company.taxNumber);
    }
  }, [company?.taxNumber]);

  // トークン確認
  const { mutate: verifyByToken } = trpc.company.verifyBillingEmailToken.useMutation({
    onSuccess: async () => {
      messageApi.success(MESSAGES.verificationSuccess);
      await utils.company.getCurrentCompany.invalidate();
    },
    onError: (error) => {
      messageApi.error(error?.message ?? MESSAGES.verificationLinkInvalid);
    },
  });
  // URLパラメータから確認トークンを取得
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('verify_token');

    if (token) {
      // 自動確認処理を実行
      verifyByToken({ token });

      // URLからトークンを削除
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [verifyByToken]);

  // 🆕 契約処理中フラグのロード（ページリロード時の復元）
  React.useEffect(() => {
    if (!company?.id) {
      return;
    }

    const state = getSubscriptionProcessing(company.id);
    if (state) {
      setIsSubscriptionProcessing(true);
      setProcessingState(state);
      logger.info('[BaseContractPanel] Restored processing state from localStorage', state);
    }
  }, [company?.id]);

  // 🆕 契約完了検知（stripeSubscriptionId設定時）
  React.useEffect(() => {
    if (!company?.id || !isSubscriptionProcessing) {
      return;
    }

    if (company.stripeSubscriptionId) {
      // 契約が完了している
      clearSubscriptionProcessing(company.id);
      setIsSubscriptionProcessing(false);
      setProcessingState(null);
      messageApi.success('契約が正常に完了しました！');
    }
  }, [company?.id, company?.stripeSubscriptionId, isSubscriptionProcessing, messageApi]);

  // 🆕 自動ポーリング（3秒ごとに会社情報を取得）
  React.useEffect(() => {
    if (!isSubscriptionProcessing || !company?.id) {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const result = await utils.company.getCurrentCompany.fetch();

        // stripeSubscriptionIdが設定されたら処理完了
        if (result?.stripeSubscriptionId) {
          clearSubscriptionProcessing(company.id);
          setIsSubscriptionProcessing(false);
          setProcessingState(null);
          // 通知は他の場所（handleCardPaymentSuccessまたはuseEffect:506）で表示されるため、ここでは表示しない
          clearInterval(interval);
        }
      } catch (error) {
        console.error('[BaseContractPanel] Auto-polling failed', error);
      }
    }, 3000); // 3秒ごと

    return () => clearInterval(interval);
  }, [isSubscriptionProcessing, company?.id, utils, messageApi]);

  if (!company || !displayState) {
    return <div>会社情報を読み込み中...</div>;
  }

  // 開発環境でデバッグ情報を出力
  if (process.env.NODE_ENV === 'development') {
    logger.debug('[BaseContractPanel] Company data:', {
      id: company.id,
      name: company.name,
      billingEmail: company.billingEmail,
      billingEmailVerified: company.billingEmailVerified,
      billingEmailVerifiedAt: company.billingEmailVerifiedAt,
      taxNumber: company.taxNumber,
    });
    logger.debug('[BaseContractPanel] Raw company object:', company);
  }

  const {
    subscriptionStatus: subscriptionStatusForDisplay,
    paymentMethod: defaultPaymentMethod,
    showPastDueAlert,
    showNoSubscriptionAlert,
    showStripeCheckout,
    showBankTransferForm,
    showBankTransferInstructions,
  } = displayState;

  const updatePlanMutation = trpc.company.updateSubscription.useMutation({
    onSuccess: async (data) => {
      // ダウングレードの場合は翌月適用のメッセージを表示
      if (data?.changeType === 'downgrade') {
        messageApi.success('プラン変更を予約しました。次回請求日から新しいプランが適用されます。');
      } else {
        messageApi.success(MESSAGES.planUpdateSuccess);
      }
      await utils.company.getCurrentCompany.invalidate();
    },
    onError: (error) => {
      // ダウングレード制約エラーの特別処理
      if (error?.data?.code === 'PRECONDITION_FAILED') {
        messageApi.error('プラン変更中に使用状況が変化しました。ページを再読み込みして最新の状態をご確認ください。');
      } else {
        messageApi.error(error?.message ?? MESSAGES.planUpdateFailure);
      }
    },
  });

  const createCheckoutSessionMutation = trpc.stripe.createCheckoutSession.useMutation({
    onError: (error) => {
      messageApi.error(error?.message ?? MESSAGES.checkoutStartFailure);
    },
  });

  const createBankTransferSubscriptionMutation = trpc.stripe.createBankTransferSubscription.useMutation({
    onSuccess: async (result) => {
      messageApi.success(MESSAGES.bankTransferSuccess);
      const payload = {
        result,
        planCode: selectedPlan,
        companyName: company?.name ?? '',
        savedAt: new Date().toISOString(),
      };
      try {
        sessionStorage.setItem(BANK_TRANSFER_STORAGE_KEY, JSON.stringify(payload));
      } catch (storageError) {
        console.warn('[BaseContractPanel] Failed to persist bank transfer result', storageError);
      }
      await utils.company.getCurrentCompany.invalidate();
      navigate('/app/subscription/bank-transfer/confirmation', { state: payload });
    },
    onError: (error) => {
      messageApi.error(error?.message ?? MESSAGES.bankTransferFailure);
    },
  });

  // 請求先メール更新
  const updateBillingEmailMutation = trpc.company.updateBillingEmail.useMutation({
    onSuccess: async (data) => {
      messageApi.success(MESSAGES.billingEmailUpdateSuccess);

      if (data.emailChanged) {
        messageApi.info('メールアドレスが変更されたため、確認が必要です');
        setVerificationEmailSent(false);
        setVerificationCode('');
      }

      await utils.company.getCurrentCompany.invalidate();
    },
    onError: (error) => {
      messageApi.error(error?.message ?? MESSAGES.billingEmailUpdateFailure);
    },
  });

  // メール確認送信
  const sendVerificationEmailMutation = trpc.company.sendBillingEmailVerification.useMutation({
    onSuccess: () => {
      messageApi.success(MESSAGES.verificationEmailSent);
      setVerificationEmailSent(true);
    },
    onError: (error) => {
      messageApi.error(error?.message ?? MESSAGES.verificationEmailFailure);
    },
  });

  // コード確認
  const verifyCodeMutation = trpc.company.verifyBillingEmailCode.useMutation({
    onSuccess: async () => {
      messageApi.success(MESSAGES.verificationSuccess);
      setVerificationCode('');
      setVerificationEmailSent(false);
      await utils.company.getCurrentCompany.invalidate();

      // Step1完了時、Step2を自動で開く
      const step1Completed = company?.taxNumber; // メール確認完了 + 法人番号あり
      const hasStripeSubscription = !!company?.stripeSubscriptionId;
      if (step1Completed && !hasStripeSubscription) {
        setActiveCollapseKeys(['plan-selection']); // Step2を開く
      }
    },
    onError: (error) => {
      messageApi.error(error?.message ?? MESSAGES.verificationCodeInvalid);
    },
  });
  // 法人番号更新
  const updateTaxNumberMutation = trpc.company.updateTaxNumber.useMutation({
    onSuccess: async () => {
      messageApi.success(MESSAGES.taxNumberUpdateSuccess);
      await utils.company.getCurrentCompany.invalidate();

      // Step1完了時、Step2を自動で開く
      const step1Completed = company?.billingEmailVerified; // 法人番号更新完了 + メール確認済み
      const hasStripeSubscription = !!company?.stripeSubscriptionId;
      if (step1Completed && !hasStripeSubscription) {
        setActiveCollapseKeys(['plan-selection']); // Step2を開く
      }
    },
    onError: (error) => {
      messageApi.error(error?.message ?? MESSAGES.taxNumberUpdateFailure);
    },
  });

  // ご紹介コード検証
  const validatePromotionCodeMutation = trpc.stripe.validatePromotionCode.useMutation({
    onSuccess: (data) => {
      setPromotionCodeValidated(true);
      setPromotionCodeValid(data.valid);
      if (data.valid) {
        setPromotionCodeMessage('適用されました');
        setPromotionCodeDiscount(data.discountInfo || null);
        messageApi.success('ご紹介コードが適用されました');
      } else {
        setPromotionCodeMessage(data.reason || 'ご利用できないコードです');
        setPromotionCodeDiscount(null);
        messageApi.warning(data.reason || 'ご利用できないコードです');
      }
    },
    onError: (error) => {
      setPromotionCodeValidated(true);
      setPromotionCodeValid(false);
      setPromotionCodeDiscount(null);
      const errorMessage = error?.message ?? 'コードの検証に失敗しました';
      setPromotionCodeMessage(errorMessage);
      messageApi.error(errorMessage);
    },
  });

  const startSubscriptionLoading =
    createCheckoutSessionMutation.isPending || createBankTransferSubscriptionMutation.isPending;

  const bankTransferSelectionEnabled = featureFlags.bankTransfer && featureFlags.bankTransferSelection;

  const effectivePaymentMethod: PaymentMethodOption =
    bankTransferSelectionEnabled
      ? (canSelectPaymentMethod
        ? paymentMethod
        : defaultPaymentMethod === 'bank_transfer'
          ? 'bank_transfer'
          : 'credit_card')
      : 'credit_card';

  const primaryActionIcon = effectivePaymentMethod === 'bank_transfer' ? <BankOutlined /> : <CreditCardOutlined />;
  const primaryActionLabel =
    effectivePaymentMethod === 'bank_transfer' ? '銀行振込で申し込む' : 'Stripeでサブスク契約を開始';
  const subscriptionHelpText = hasStripeSubscription
    ? '変更後はStripeに即時反映され、差額は日割りで精算されます。'
    : effectivePaymentMethod === 'bank_transfer'
      ? '選択したプランで銀行振込によるお申し込みを開始します。振込先情報は次の画面で確認できます。'
      : '選択したプランでStripe Checkoutに遷移します。完了後に契約情報が自動で反映されます。';

  const handlePlanUpdate = async () => {
    if (!hasStripeSubscription) {
      messageApi.warning(MESSAGES.stripeNotLinked);
      return;
    }
    if (!canChangePlan) {
      messageApi.warning(MESSAGES.blockedPlanChange);
      return;
    }
    if (!planChanged) {
      messageApi.info(MESSAGES.noPlanChange);
      return;
    }
    // 確認モーダルを開く
    setPlanChangeConfirmOpen(true);
  };

  const handleConfirmPlanChange = async () => {
    try {
      await updatePlanMutation.mutateAsync({
        companyId: company.id,
        plan: selectedPlan,
      });
      setPlanChangeConfirmOpen(false);
      // 成功メッセージは updatePlanMutation の onSuccess で表示されるため、ここでは表示しない
    } catch (error) {
      console.error(error);
      // エラーメッセージは mutation の onError で表示される
    }
  };

  const handleStartSubscription = async () => {
    if (!planSelectionValid) {
      messageApi.warning(MESSAGES.invalidPlanSelection);
      return;
    }

    // 既存のサブスクリプションがある場合はブロック
    if (hasStripeSubscription) {
      messageApi.warning('既にサブスクリプションが存在します。プラン変更をご希望の場合は、契約管理画面からお手続きください。');
      return;
    }

    if (!canSelectPaymentMethod && hasPendingBankTransfer) {
      messageApi.warning(MESSAGES.bankTransferPending);
      return;
    }

    // 無料登録中は請求先メールのチェックをスキップ
    const isTrialing = displayState?.subscriptionStatus === 'trialing';

    // 請求先メールの必須チェック（無料登録以外）
    if (!isTrialing && !preferredCustomerEmail) {
      messageApi.error(MESSAGES.missingBillingEmail);
      return;
    }

    // 請求先メールの確認済みチェック（無料登録以外）
    if (!isTrialing && !company?.billingEmailVerified) {
      messageApi.error(MESSAGES.billingEmailNotVerified);
      return;
    }

    // 法人番号の必須チェック（無料登録以外）
    if (!isTrialing && !company?.taxNumber) {
      messageApi.error(MESSAGES.missingTaxNumber);
      return;
    }

    if (effectivePaymentMethod === 'bank_transfer') {
      if (!company.id) {
        messageApi.error(MESSAGES.companyNotLoaded);
        return;
      }
      try {
        const metadata: Record<string, string> = {
          request_source: 'company_settings_subscription_tab',
        };
        if (currentUser?.id) {
          metadata.request_user_id = currentUser.id;
        }
        if (currentUser?.email) {
          metadata.request_user_email = currentUser.email;
        }
        const normalizedCustomerName = (preferredCustomerName || company.name || '不明な会社名').trim();
        const parseResult = bankTransferRequestSchema.safeParse({
          planCode: selectedPlan,
          customerEmail: preferredCustomerEmail,
          customerName: normalizedCustomerName,
          companyId: company.id,
          predictionAddonQuantity: 0,
          userAddonQuantity: company.subscriptionUserAddonUnits ?? 0,
          daysUntilDue: 30,
        });
        if (!parseResult.success) {
          const firstIssue = parseResult.error.issues[0];
          messageApi.error(firstIssue?.message ?? MESSAGES.bankTransferFailure);
          return;
        }
        await createBankTransferSubscriptionMutation.mutateAsync({
          ...parseResult.data,
          planCode: parseResult.data.planCode as (typeof MANAGEABLE_PLAN_CODES)[number],
          metadata,
        });
      } catch (error) {
        console.error(error);
      }
      return;
    }

    // クレジットカード決済モーダルを開く
    setCardPaymentModalOpen(true);
  };

  const handleCardPaymentSuccess = async (_subscriptionId: string) => {
    setCardPaymentModalOpen(false);

    // 🆕 処理中フラグを設定
    if (company?.id) {
      setSubscriptionProcessing(company.id, selectedPlan, 'card');
      setIsSubscriptionProcessing(true);
    }

    const hideLoading = messageApi.loading('契約情報を反映中です。自動的に更新されます...', 0);

    try {
      // 会社情報を再取得
      await utils.company.getCurrentCompany.invalidate();

      // 少し待ってから再取得（webhook処理を待つ）
      await new Promise(resolve => setTimeout(resolve, 2000));
      const result = await utils.company.getCurrentCompany.fetch();

      // すぐに反映された場合はリロード不要
      if (result?.stripeSubscriptionId) {
        if (company?.id) {
          clearSubscriptionProcessing(company.id);
          setIsSubscriptionProcessing(false);
        }
        hideLoading();
        // 通知はuseEffect（506行目）で表示されるため、ここでは表示しない
        return;
      }
    } catch (error) {
      console.error('[BaseContractPanel] Failed to refetch company', error);
    } finally {
      hideLoading();
    }

    // リロード（処理中フラグは保持される）
    // リロード後、useEffect（506行目）で契約完了の通知が表示される
    window.location.reload();
  };

  // 請求先メール保存
  const handleUpdateBillingEmail = async () => {
    if (!company?.id) {
      messageApi.error(MESSAGES.companyNotLoaded);
      return;
    }

    const trimmedEmail = editingBillingEmail.trim();
    if (!isValidEmail(trimmedEmail)) {
      messageApi.error(MESSAGES.invalidBillingEmail);
      return;
    }

    try {
      await updateBillingEmailMutation.mutateAsync({
        companyId: company.id,
        billingEmail: trimmedEmail,
      });
    } catch (error) {
      console.error('[BaseContractPanel] Failed to update billing email', error);
    }
  };

  // 確認メール送信
  const handleSendVerificationEmail = async () => {
    if (!company?.id) {
      messageApi.error(MESSAGES.companyNotLoaded);
      return;
    }

    const emailToVerify = preferredCustomerEmail;
    if (!emailToVerify) {
      messageApi.error('請求先メールアドレスが設定されていません');
      return;
    }

    try {
      await sendVerificationEmailMutation.mutateAsync({
        companyId: company.id,
        email: emailToVerify,
      });
    } catch (error) {
      console.error('[BaseContractPanel] Failed to send verification email', error);
    }
  };

  // コード確認
  const handleVerifyCode = async () => {
    if (!company?.id) {
      messageApi.error(MESSAGES.companyNotLoaded);
      return;
    }

    try {
      await verifyCodeMutation.mutateAsync({
        companyId: company.id,
        code: verificationCode.trim(),
      });
    } catch (error) {
      console.error('[BaseContractPanel] Failed to verify code', error);
    }
  };

  // 法人番号保存
  const handleUpdateTaxNumber = async () => {
    if (!company?.id) {
      messageApi.error(MESSAGES.companyNotLoaded);
      return;
    }

    const trimmedTaxNumber = editingTaxNumber.trim();
    if (!isValidTaxNumber(trimmedTaxNumber)) {
      messageApi.error(MESSAGES.invalidTaxNumber);
      return;
    }

    try {
      await updateTaxNumberMutation.mutateAsync({
        companyId: company.id,
        taxNumber: trimmedTaxNumber,
      });
    } catch (error) {
      console.error('[BaseContractPanel] Failed to update tax number', error);
    }
  };

  // ご紹介コード検証
  const handleValidatePromotionCode = async () => {
    const trimmedCode = promotionCode.trim();
    if (!trimmedCode) {
      messageApi.warning('ご紹介コードを入力してください');
      return;
    }

    try {
      await validatePromotionCodeMutation.mutateAsync({
        code: trimmedCode,
      });
    } catch (error) {
      console.error('[BaseContractPanel] Failed to validate promotion code', error);
    }
  };

  return (
    <>
      {messageContextHolder}
      <Space direction="vertical" size="large" style={{ width: '100%' }}>

        {showPastDueAlert && (
          <Alert
            message="お支払いの確認が取れていません"
            description="サービスを継続してご利用いただくため、お支払い方法を更新してください。"
            type="error"
            showIcon
          />
        )}

        {showBankTransferInstructions && hasPendingBankTransfer && (
          <Alert
            message="銀行振込のお手続き中です"
            description="振込先情報をご確認のうえ、ご入金が完了するまでしばらくお待ちください。入金確認後に契約が自動で有効化されます。"
            type="info"
            showIcon
          />
        )}

        {showNoSubscriptionAlert && (
          <Alert
            message="まだ有効な契約がありません"
            description="下のボタンから支払い方法を選択し、契約を開始してください。"
            type="info"
            showIcon
          />
        )}

        {/* 🆕 契約処理中の警告 */}
        {isSubscriptionProcessing && processingState && (
          <Alert
            type="warning"
            showIcon
            message="契約処理を実行中です"
            description={
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>
                  Stripeでの契約処理が進行中です。
                  通常、数秒〜数分で完了し、自動的に更新されます。
                </Text>
                <Divider style={{ margin: '8px 0' }} />
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="プラン">
                    {PLAN_DEFINITIONS[processingState.planCode]?.name ?? processingState.planCode}
                  </Descriptions.Item>
                  <Descriptions.Item label="開始時刻">
                    {new Date(processingState.startedAt).toLocaleString('ja-JP')}
                  </Descriptions.Item>
                </Descriptions>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  ※ 10分以上経過しても反映されない場合は、サポートまでお問い合わせください
                </Text>
                <Button
                  size="small"
                  onClick={() => window.location.reload()}
                  icon={<ReloadOutlined />}
                >
                  ページを手動で再読み込み
                </Button>
              </Space>
            }
          />
        )}

        {showStripeCheckout && !hasStripeSubscription && !hasPendingBankTransfer && !isSubscriptionProcessing && (
          <Alert
            message="Stripe契約が未連携です"
            description="下のボタンからStripe Checkoutを開始し、サブスク契約を完了してください。完了後にこの画面を再読込すると最新の契約情報が表示されます。"
            type="warning"
            showIcon
          />
        )}

        {/* [v2.1 新規] 解約申請中の警告 */}
        {isCancellationPending && (
          <Alert
            message="解約申請中のため、プラン変更はできません"
            description={
              <Space direction="vertical" size="small">
                <Text>
                  現在、解約申請が受理されています。
                  {(company?.cancelEffectiveDate || latestRequest?.effectiveDate) && (
                    <>
                      <strong>{new Date(company?.cancelEffectiveDate ?? latestRequest?.effectiveDate ?? '').toLocaleDateString('ja-JP')}</strong> をもってサービスが停止されます。
                    </>
                  )}
                </Text>
                <Text>
                  プラン変更を行う場合は、先に解約を取り消してください。
                </Text>
              </Space>
            }
            type="warning"
            showIcon
            action={
              <Button size="small" onClick={() => onSwitchToTab?.('cancellation')}>
                解約タブを開く
              </Button>
            }
          />
        )}

        {/* 現在のプラン情報（シンプル化） */}
        <Card>
          <Row gutter={[24, 16]}>
            {/* 左側: 主要情報 */}
            <Col xs={24} md={12}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '14px' }}>現在のプラン</Text>
                  <div style={{ marginTop: 4 }}>
                    <Tag color={currentPlan.color} style={{ fontSize: '16px', padding: '4px 12px' }}>
                      {currentPlan.name}
                    </Tag>
                    <Tag color={getSubscriptionStatusColor(subscriptionStatusForDisplay)} style={{ marginLeft: 8 }}>
                      {getSubscriptionStatusLabel(subscriptionStatusForDisplay)}
                    </Tag>
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <Text type="secondary" style={{ fontSize: '14px' }}>月額料金</Text>
                  <div style={{ marginTop: 4 }}>
                    <Text strong style={{ fontSize: '24px', color: '#1890ff' }}>
                      {currentPlanPriceParts.base}
                    </Text>
                    {currentPlanPriceParts.tax && (
                      <Text type="secondary" style={{ marginLeft: 8, fontSize: '14px' }}>
                        {currentPlanPriceParts.tax}
                      </Text>
                    )}
                  </div>
                </div>

                {/* 予約プラン情報の表示 */}
                {company.scheduledPlanCode && company.scheduledPlanEffectiveDate && (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                    message={
                      <Space direction="vertical" size={0}>
                        <Text strong>プラン変更予約中</Text>
                        <Text>
                          {new Date(company.scheduledPlanEffectiveDate).toLocaleDateString('ja-JP')} から{' '}
                          <Tag color={getPlanDetails(company.scheduledPlanCode).color}>
                            {getPlanDetails(company.scheduledPlanCode).name}
                          </Tag>
                          に変更されます
                        </Text>
                      </Space>
                    }
                  />
                )}

                {/* 機能リストをコンパクトに */}
                <Collapse
                  ghost
                  size="small"
                  style={{ marginTop: 8 }}
                  items={[
                    {
                      key: 'features',
                      label: <Text type="secondary">主な機能を表示</Text>,
                      children: (
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {currentPlan.features.map((feature, index) => (
                            <li key={index}><Text type="secondary">{feature}</Text></li>
                          ))}
                        </ul>
                      ),
                    },
                  ]}
                />
              </Space>
            </Col>

            {/* 右側: 契約情報 */}
            <Col xs={24} md={12}>
              <Descriptions column={1} size="small">
                {company.subscriptionStartsAt && (
                  <Descriptions.Item label="契約開始日">
                    {new Date(company.subscriptionStartsAt).toLocaleDateString('ja-JP')}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="次回請求日">
                  {subscriptionStatusForDisplay === 'active' && company.subscriptionEndsAt
                    ? new Date(company.subscriptionEndsAt).toLocaleDateString('ja-JP')
                    : subscriptionStatusForDisplay === 'active' ? '毎月1日' : '-'}
                </Descriptions.Item>
                {company.scheduledPlanCode && company.scheduledPlanEffectiveDate && (
                  <Descriptions.Item label="予約プラン適用日">
                    {new Date(company.scheduledPlanEffectiveDate).toLocaleDateString('ja-JP')}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Col>
          </Row>
        </Card>

        {isManager && (
          <Card title="契約プランの変更">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* 進捗ステップ */}
              <Steps
                current={
                  !preferredCustomerEmail || !company.billingEmailVerified
                    ? 0
                    : !company.taxNumber
                      ? 0
                      : hasStripeSubscription
                        ? 2
                        : 1
                }
                items={[
                  {
                    title: '請求先情報',
                    icon: <MailOutlined />,
                    description: preferredCustomerEmail && company.billingEmailVerified && company.taxNumber
                      ? '設定完了'
                      : '請求先メール・法人番号を設定',
                  },
                  {
                    title: 'プラン選択',
                    icon: <CreditCardOutlined />,
                    description: hasStripeSubscription ? '契約中' : 'プランを選択',
                  },
                  {
                    title: '契約開始',
                    icon: <CheckCircleOutlined />,
                    description: hasStripeSubscription ? '契約済み' : '契約を開始',
                  },
                ]}
              />

              {/* Step 1: 請求先情報 */}
              <Collapse
                activeKey={activeCollapseKeys}
                onChange={(keys) => setActiveCollapseKeys(keys as string[])}
                items={[
                  {
                    key: 'billing-info',
                    label: (
                      <Space>
                        <Text strong>Step 1: 請求先情報の設定</Text>
                        {preferredCustomerEmail && company.billingEmailVerified && company.taxNumber ? (
                          <Tag color="success" icon={<CheckCircleOutlined />}>完了（編集可能）</Tag>
                        ) : (
                          <Tag color="warning">必須</Tag>
                        )}
                      </Space>
                    ),
                    children: (
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {/* 請求先メールアドレスセクション */}
                        <div>
                          <Text strong>請求先メールアドレス</Text>
                          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                            請求書および振込先情報の送信先として使用されます。専用の請求用メールアドレスの使用を推奨します。
                          </Text>

                          {!preferredCustomerEmail && (
                            <Alert
                              type="warning"
                              showIcon
                              message="請求先メールアドレスが未設定です"
                              description="契約を開始するには、請求先メールアドレスの設定が必要です。"
                              style={{ marginBottom: 12 }}
                            />
                          )}

                          <Space.Compact style={{ width: '100%' }}>
                            <Input
                              type="email"
                              value={editingBillingEmail}
                              onChange={(e) => setEditingBillingEmail(e.target.value)}
                              placeholder="billing@example.com"
                              prefix={<MailOutlined />}
                              status={!preferredCustomerEmail ? 'error' : undefined}
                            />
                            <Button
                              type="primary"
                              onClick={handleUpdateBillingEmail}
                              loading={updateBillingEmailMutation.isPending}
                              disabled={!isValidEmail(editingBillingEmail)}
                            >
                              保存
                            </Button>
                          </Space.Compact>

                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                            例: keiri@company.co.jp, invoice@company.co.jp
                          </Text>

                          {/* メール確認ステータス表示 */}
                          {preferredCustomerEmail && (
                            <>
                              {company.billingEmailVerified ? (
                                <Alert
                                  type="success"
                                  showIcon
                                  message="✅ 確認済み"
                                  description={
                                    <Space direction="vertical" size={0}>
                                      <Text>確認日時: {formatDateTimeForDisplay(company.billingEmailVerifiedAt)}</Text>
                                      <Text type="secondary" style={{ fontSize: '12px' }}>
                                        ※ メールアドレスを変更した場合は、再度確認が必要になります
                                      </Text>
                                    </Space>
                                  }
                                  style={{ marginTop: 8 }}
                                />
                              ) : (
                                <>
                                  <Alert
                                    type="warning"
                                    showIcon
                                    message="⚠️ 未確認"
                                    description="このメールアドレスはまだ確認されていません。契約を開始するには確認が必要です。"
                                    style={{ marginTop: 8 }}
                                  />
                                  <Button
                                    icon={<MailOutlined />}
                                    onClick={handleSendVerificationEmail}
                                    loading={sendVerificationEmailMutation.isPending}
                                    size="small"
                                    style={{ marginTop: 8 }}
                                  >
                                    確認メールを送信
                                  </Button>
                                </>
                              )}

                              {/* 確認コード入力UI */}
                              {verificationEmailSent && (
                                <div style={{ marginTop: 12 }}>
                                  <Text strong>確認コードを入力してください</Text>
                                  <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginTop: 4 }}>
                                    {preferredCustomerEmail} 宛に確認メールを送信しました
                                  </Text>
                                  <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                                    <Input
                                      placeholder="6桁のコード"
                                      value={verificationCode}
                                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                                      maxLength={6}
                                      style={{ width: '150px' }}
                                    />
                                    <Button
                                      type="primary"
                                      onClick={handleVerifyCode}
                                      loading={verifyCodeMutation.isPending}
                                      disabled={verificationCode.length !== 6}
                                    >
                                      確認する
                                    </Button>
                                  </Space.Compact>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* 法人番号セクション */}
                        <div>
                          <Text strong>法人番号</Text>
                          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                            請求書および領収書に記載されます。13桁の法人番号を入力してください。
                          </Text>

                          {!company?.taxNumber && (
                            <Alert
                              type="warning"
                              showIcon
                              message="法人番号が未設定です"
                              description="契約を開始するには、法人番号の設定が必要です。"
                              style={{ marginBottom: 12 }}
                            />
                          )}

                          <Space.Compact style={{ width: '100%' }}>
                            <Input
                              value={editingTaxNumber}
                              onChange={(e) => setEditingTaxNumber(e.target.value.replace(/\D/g, ''))}
                              placeholder="1234567890123"
                              maxLength={13}
                              status={!company?.taxNumber ? 'error' : undefined}
                            />
                            <Button
                              type="primary"
                              onClick={handleUpdateTaxNumber}
                              loading={updateTaxNumberMutation.isPending}
                              disabled={!isValidTaxNumber(editingTaxNumber)}
                            >
                              保存
                            </Button>
                          </Space.Compact>

                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                            ※ 国税庁の法人番号公表サイトで確認できます
                          </Text>
                        </div>
                      </Space>
                    ),
                  },
                ]}
              />

              {/* Step 2: プラン選択 */}
              <Collapse
                activeKey={activeCollapseKeys}
                onChange={(keys) => setActiveCollapseKeys(keys as string[])}
                items={[
                  {
                    key: 'plan-selection',
                    label: (
                      <Space>
                        <Text strong>Step 2: プラン選択</Text>
                        {hasStripeSubscription && (
                          <Tag color="success" icon={<CheckCircleOutlined />}>契約中</Tag>
                        )}
                      </Space>
                    ),
                    collapsible: !preferredCustomerEmail || !company.billingEmailVerified || !company.taxNumber
                      ? 'disabled'
                      : undefined,
                    children: (
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {(!preferredCustomerEmail || !company.billingEmailVerified || !company.taxNumber) && (
                          <Alert
                            type="info"
                            message="まず請求先情報を設定してください"
                            description="プラン選択を行う前に、Step 1で請求先メールアドレスと法人番号を設定し、メールアドレスの確認を完了してください。"
                          />
                        )}
                        <Select
                          value={selectedPlan}
                          onChange={(value) => setSelectedPlan(resolveManagedPlanCode(value))}
                          options={planOptions}
                          style={{ width: '100%' }}
                          disabled={updatePlanMutation.isPending || startSubscriptionLoading}
                        />
                        {selectedPlanDetails.description && (
                          <Paragraph style={{ marginBottom: 0 }} type="secondary">
                            {selectedPlanDetails.description}
                          </Paragraph>
                        )}
                        <Text type="secondary">主な機能</Text>
                        <ul className="list-disc list-inside" style={{ marginTop: 0 }}>
                          {selectedPlanDetails.features.map((feature, index) => (
                            <li key={feature + index}>{feature}</li>
                          ))}
                        </ul>
                        <Text strong>{selectedPlanPriceParts.base}</Text>
                        {selectedPlanPriceParts.tax && (
                          <Text type="secondary" style={{ display: 'block' }}>
                            ({selectedPlanPriceParts.tax})
                          </Text>
                        )}
                        <Text type="secondary">{subscriptionHelpText}</Text>

                        {/* ダウングレード時の注意事項 */}
                        {planChanged && hasStripeSubscription && (() => {
                          const currentPlanIndex = MANAGED_PLAN_ORDER.indexOf(currentPlanKey as ManagedPlanCode);
                          const selectedPlanIndex = MANAGED_PLAN_ORDER.indexOf(selectedPlan as ManagedPlanCode);
                          const isDowngrade = currentPlanIndex !== -1 && selectedPlanIndex !== -1 && selectedPlanIndex < currentPlanIndex;

                          if (isDowngrade) {
                            return (
                              <Alert
                                type="info"
                                showIcon
                                message="ダウングレードの注意事項"
                                description="ダウングレード時は、新プランの設備数・ストレージ容量制限内に収める必要があります。詳細は「契約プランを更新」ボタンクリック後の確認画面でご確認いただけます。"
                                style={{ marginTop: 12 }}
                              />
                            );
                          }
                          return null;
                        })()}

                        {/* ご紹介コード入力セクション */}
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Text strong>ご紹介コード（オプション）</Text>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                            ※ 代理店から提供されたコードを入力してください
                          </Text>
                          <Space.Compact style={{ width: '100%' }}>
                            <Input
                              value={promotionCode}
                              onChange={(e) => {
                                setPromotionCode(e.target.value.toUpperCase());
                                setPromotionCodeValidated(false);
                                setPromotionCodeValid(null);
                                setPromotionCodeMessage('');
                                setPromotionCodeDiscount(null);
                              }}
                              placeholder="例: ABC123"
                              maxLength={64}
                              style={{ textTransform: 'uppercase' }}
                            />
                            <Button
                              onClick={handleValidatePromotionCode}
                              loading={validatePromotionCodeMutation.isPending}
                              disabled={!promotionCode.trim()}
                            >
                              確認
                            </Button>
                          </Space.Compact>
                          {promotionCodeValidated && (
                            <Alert
                              type={promotionCodeValid ? 'success' : 'warning'}
                              message={promotionCodeMessage}
                              showIcon
                              style={{ marginTop: 8 }}
                            />
                          )}
                        </Space>

                        {/* 銀行振込が有効な場合のみ支払い方法選択を表示 */}
                        {showBankTransferForm && bankTransferSelectionEnabled && (
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong>お支払い方法</Text>
                            <Radio.Group
                              value={canSelectPaymentMethod ? paymentMethod : effectivePaymentMethod}
                              onChange={(event) => setPaymentMethod(event.target.value as PaymentMethodOption)}
                              disabled={startSubscriptionLoading || !canSelectPaymentMethod}
                            >
                              <Space direction="vertical">
                                <Radio value="credit_card">
                                  クレジットカード
                                  <Text type="secondary" style={{ marginLeft: 8 }}>
                                    即時に利用開始できます
                                  </Text>
                                </Radio>
                                <Radio value="bank_transfer">
                                  銀行振込
                                  <Text type="secondary" style={{ marginLeft: 8 }}>
                                    入金確認後にサービスが有効化されます
                                  </Text>
                                </Radio>
                              </Space>
                            </Radio.Group>
                            {!canSelectPaymentMethod && hasPendingBankTransfer && (
                              <Text type="secondary">
                                銀行振込の確認中は支払い方法を変更できません。
                              </Text>
                            )}
                            {effectivePaymentMethod === 'bank_transfer' && (
                              <Alert
                                type="info"
                                showIcon
                                message="銀行振込について"
                                description={
                                  <Space direction="vertical" size={0}>
                                    <Text>入金確認まで通常3〜5営業日かかります。請求先メールアドレス宛に請求書と振込先情報をお送りします。</Text>
                                    <Text>初回利用月は日割り計算、２ヶ月目以降は月末に翌月分を請求いたします。</Text>
                                  </Space>
                                }
                              />
                            )}
                          </Space>
                        )}
                        <Space>
                          {hasStripeSubscription ? (
                            <Button
                              type="primary"
                              icon={<SwapOutlined />}
                              onClick={handlePlanUpdate}
                              loading={updatePlanMutation.isPending}
                              disabled={!canChangePlan || !planChanged || updatePlanMutation.isPending || isCancellationPending}
                            >
                              契約プランを更新
                            </Button>
                          ) : (
                            <Button
                              type="primary"
                              icon={primaryActionIcon}
                              onClick={handleStartSubscription}
                              loading={startSubscriptionLoading}
                              disabled={
                                !planSelectionValid ||
                                startSubscriptionLoading ||
                                hasStripeSubscription ||  // 既存サブスクリプションがある場合は無効化
                                (!canSelectPaymentMethod && hasPendingBankTransfer) ||
                                isSubscriptionProcessing  // 🆕 追加: 処理中は無効化
                              }
                            >
                              {isSubscriptionProcessing ? '処理中...' : primaryActionLabel}
                            </Button>
                          )}
                          {ENTERPRISE_PREMIUM_ENABLED && (
                            <Button
                              icon={<RocketOutlined />}
                              onClick={() => setSelectedPlan('enterprise_premium')}
                              disabled={updatePlanMutation.isPending || startSubscriptionLoading}
                            >
                              最上位プランに切り替える
                            </Button>
                          )}
                        </Space>
                        {(!canChangePlan || isCancellationPending) && hasStripeSubscription && (
                          <Text type="secondary">
                            {isCancellationPending
                              ? '解約申請中のため、プランを変更できません。解約を取り消してから変更してください。'
                              : '現在の契約状態ではプランを変更できません。支払い状況をご確認ください。'}
                          </Text>
                        )}
                      </Space>
                    ),
                  },
                ]}
              />
            </Space>
          </Card>
        )}

        {/* 銀行振込が有効な場合のみ銀行振込状況カードを表示 */}
        {isManager && showBankTransferStatusCard && featureFlags.bankTransfer && (
          <Card title="銀行振込のお申し込み状況">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {bankTransferStatusUi && (
                <Space direction="vertical" size="small">
                  <Space align="center" size="middle">
                    <Tag color={bankTransferStatusUi.tagColor}>{bankTransferStatusUi.label}</Tag>
                    {bankTransferStatusUpdatedLabel && (
                      <Text type="secondary">
                        最終更新: {bankTransferStatusUpdatedLabel}
                      </Text>
                    )}
                  </Space>
                  <Text>{bankTransferStatusUi.description}</Text>
                </Space>
              )}
              {company.bankTransferMemo && company.bankTransferMemo.trim().length > 0 && (
                <Alert
                  type="info"
                  showIcon
                  message="運営からのメモ"
                  description={company.bankTransferMemo}
                />
              )}
              {hasStoredBankTransferResult && (
                <Space direction="vertical" size="small">
                  <Alert
                    type="warning"
                    showIcon
                    message="振込先情報を再確認できます"
                    description="最新の請求書と振込先の口座情報をいつでも確認できます。入金前に内容をご確認ください。"
                  />
                  <Space align="center" wrap>
                    <Button
                      icon={<FileTextOutlined />}
                      onClick={() => navigate('/app/subscription/bank-transfer/confirmation')}
                    >
                      振込先情報を開く
                    </Button>
                    {bankTransferSavedLabel && (
                      <Text type="secondary">
                        保存日時: {bankTransferSavedLabel}
                      </Text>
                    )}
                  </Space>
                </Space>
              )}
            </Space>
          </Card>
        )}

        {company.stripeCustomerId && (
          <Card title="支払い情報">
            <Descriptions column={1}>
              <Descriptions.Item label="Stripe顧客ID">
                {company.stripeCustomerId}
              </Descriptions.Item>
              {company.stripeSubscriptionId && (
                <Descriptions.Item label="Stripe契約プラン ID">
                  {company.stripeSubscriptionId}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="支払い方法">
                {company.bankTransferStatus
                  ? '銀行振込'
                  : company.stripePaymentMethodId
                    ? 'クレジットカード（登録済み）'
                    : '未設定'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {subscriptionStatusForDisplay === 'canceled' && (
          <Card
            style={{
              background: 'linear-gradient(135deg, #fff2f0 0%, #ffeded 100%)',
              border: '2px solid #ff4d4f',
            }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Alert
                message="サブスクリプションが期限切れです"
                description={
                  <Space direction="vertical" size="small">
                    <Text>サービスを継続してご利用いただくには、新しいプランをお選びください。</Text>
                    <Text type="secondary">
                      再契約後、すぐにサービスをご利用いただけます。既存のデータは保持されています。
                    </Text>
                  </Space>
                }
                type="error"
                showIcon
                style={{ marginBottom: 0 }}
              />
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<RocketOutlined />}
                  onClick={() => {
                    // Step1とStep2を開く
                    setActiveCollapseKeys(['billing-info', 'plan-selection']);
                    // ページ上部へスクロール
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  style={{
                    height: '48px',
                    fontSize: '16px',
                    padding: '0 32px',
                  }}
                >
                  サービスを再開する
                </Button>
              </div>
            </Space>
          </Card>
        )}

      </Space>

      {/* カード決済モーダル */}
      <SubscriptionCardPaymentModal
        open={cardPaymentModalOpen}
        planCode={selectedPlan}
        planName={SUBSCRIPTION_PLAN_CONFIGS[selectedPlan as ManagedPlanCode]?.label || selectedPlan}
        amount={SUBSCRIPTION_PLAN_CONFIGS[selectedPlan as ManagedPlanCode]?.monthlyPrice || 0}
        prorationAmount={calculateProrationAmount(
          SUBSCRIPTION_PLAN_CONFIGS[selectedPlan as ManagedPlanCode]?.monthlyPrice || 0
        )}
        customerEmail={preferredCustomerEmail}
        promotionCode={promotionCodeValid && promotionCodeValidated ? promotionCode : undefined}
        promotionCodeValidated={promotionCodeValidated}
        promotionCodeValid={promotionCodeValid ?? false}
        promotionCodeDiscount={promotionCodeDiscount}
        onSuccess={handleCardPaymentSuccess}
        onCancel={() => setCardPaymentModalOpen(false)}
      />

      {/* プラン変更確認モーダル */}
      <PlanChangeConfirmModal
        open={planChangeConfirmOpen}
        onCancel={() => setPlanChangeConfirmOpen(false)}
        onConfirm={handleConfirmPlanChange}
        currentPlanCode={currentPlanKey}
        newPlanCode={selectedPlan}
        loading={updatePlanMutation.isPending}
      />
    </>
  );
};

export default BaseContractPanel;
